import { useEffect } from 'react';

function ensureTag(selector, createTag) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = createTag();
    document.head.appendChild(element);
  }

  return element;
}

// Replace the full set of tags we manage under a data-attribute marker, so
// stale hreflang/og:locale alternates from the previous route never linger.
function replaceMarkedTags(marker, entries, build) {
  document.head.querySelectorAll(`[${marker}]`).forEach((el) => el.remove());
  entries.forEach((entry) => {
    const tag = build(entry);
    tag.setAttribute(marker, '');
    document.head.appendChild(tag);
  });
}

export default function Seo({
  title,
  description,
  canonical,
  robots,
  image,
  type = 'website',
  alternates = [],
  ogLocale,
  ogLocaleAlternates = [],
}) {
  const alternatesKey = JSON.stringify(alternates);
  const ogLocaleAlternatesKey = JSON.stringify(ogLocaleAlternates);
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const descriptionTag = ensureTag('meta[name="description"]', () => {
      const tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      return tag;
    });
    descriptionTag.setAttribute('content', description || '');

    const robotsTag = ensureTag('meta[name="robots"]', () => {
      const tag = document.createElement('meta');
      tag.setAttribute('name', 'robots');
      return tag;
    });
    robotsTag.setAttribute('content', robots || 'index,follow');

    const canonicalTag = ensureTag('link[rel="canonical"]', () => {
      const tag = document.createElement('link');
      tag.setAttribute('rel', 'canonical');
      return tag;
    });
    canonicalTag.setAttribute('href', canonical || window.location.href);

    // hreflang alternates — Google uses these to serve the right language and to
    // avoid treating the 6 language versions as duplicate content.
    replaceMarkedTags('data-owa-alt', alternates, ({ hreflang, href }) => {
      const tag = document.createElement('link');
      tag.setAttribute('rel', 'alternate');
      tag.setAttribute('hreflang', hreflang);
      tag.setAttribute('href', href);
      return tag;
    });

    const openGraphEntries = {
      'og:title': title || '',
      'og:description': description || '',
      'og:type': type,
      'og:url': canonical || window.location.href,
      'og:image': image ? new URL(image, window.location.origin).toString() : '',
      'og:site_name': 'OWA Wild Adventure',
      ...(ogLocale ? { 'og:locale': ogLocale } : {}),
    };

    Object.entries(openGraphEntries).forEach(([property, content]) => {
      const openGraphTag = ensureTag(`meta[property="${property}"]`, () => {
        const tag = document.createElement('meta');
        tag.setAttribute('property', property);
        return tag;
      });

      openGraphTag.setAttribute('content', content);
    });

    replaceMarkedTags('data-owa-oglocale', ogLocaleAlternates, (locale) => {
      const tag = document.createElement('meta');
      tag.setAttribute('property', 'og:locale:alternate');
      tag.setAttribute('content', locale);
      return tag;
    });

    // Twitter card mirrors the Open Graph basics.
    const twitterEntries = {
      'twitter:card': image ? 'summary_large_image' : 'summary',
      'twitter:title': title || '',
      'twitter:description': description || '',
      'twitter:image': image ? new URL(image, window.location.origin).toString() : '',
    };
    Object.entries(twitterEntries).forEach(([name, content]) => {
      const tag = ensureTag(`meta[name="${name}"]`, () => {
        const el = document.createElement('meta');
        el.setAttribute('name', name);
        return el;
      });
      tag.setAttribute('content', content);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical, description, image, robots, title, type, ogLocale, alternatesKey, ogLocaleAlternatesKey]);

  return null;
}
